import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clinicApi, clinicApiFor } from '@/lib/api';
import { useAuth } from './useAuth';
import { MEMBERSHIPS_KEY } from './useClinics';
import type {
  ClinicMember,
  ClinicRole,
  CreateRolePayload,
  SetMemberRolesPayload,
  UpdateRolePayload,
} from '@/types/api';

export const ROLES_KEY = ['roles'] as const;
const PEOPLE_KEY = ['people'] as const;

/**
 * The clinic's roles. Scoped server-side by the caller's active clinic, so no
 * clinicId travels in the request.
 *
 * Deliberately NOT gated on `roles.read`. It was, which read as a tidy
 * "don't fire a request that can only 403" — but the People page needs this list
 * to know which roles manage access, and a member holding `members.*` without
 * `roles.read` is a role a clinic can build. Gated, the query silently resolved
 * to an empty list there and the page concluded the clinic had no access
 * managers at all: the "last person who can manage access" warning vanished and
 * the Remove button un-disabled. A failed request the caller can see beats an
 * empty one they cannot.
 *
 * Callers that genuinely have nothing to do with roles pass `enabled: false`.
 *
 * `clinicSlug` overrides which clinic is asked. Only onboarding passes it: it
 * lists the roles of the clinic it has just created while the page is still in
 * the global namespace, where there is no slug in the URL to read.
 */
export function useClinicRoles(opts: { enabled?: boolean; clinicSlug?: string } = {}) {
  const { isAuthenticated, activeClinic } = useAuth();
  const slug = opts.clinicSlug ?? activeClinic?.slug ?? null;
  const enabled = (opts.enabled ?? true) && isAuthenticated && !!slug;
  return useQuery({
    queryKey: [...ROLES_KEY, 'list', slug],
    queryFn: () => clinicApiFor(slug as string).get<ClinicRole[]>('roles'),
    enabled,
    staleTime: 15_000,
  });
}

/**
 * Every role mutation invalidates three things, and the third is the one that
 * is easy to miss.
 *
 * `roles` and `people` are the visible lists: changing a role's permissions
 * changes what its holders can do, and the member list renders the roles each
 * member holds.
 *
 * `memberships` is where the CALLER's own permissions live — it feeds
 * `useAuth().permissions` and therefore every `can()` in the app, including the
 * sidebar. Editing your own role otherwise leaves the menu describing access you
 * no longer have. It used to correct itself by accident: the invalidated roles
 * query refetched, hit a bumped epoch, and the stale-token refresh replaced the
 * session wholesale. That chain breaks the moment no query happens to be active
 * — `refetchOnWindowFocus` is off — so the nav could stay wrong indefinitely.
 * Asking for it directly is the difference between a guarantee and a coincidence.
 */
function useRoleMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROLES_KEY });
      qc.invalidateQueries({ queryKey: PEOPLE_KEY });
      qc.invalidateQueries({ queryKey: MEMBERSHIPS_KEY });
    },
  });
}

export function useCreateRole() {
  return useRoleMutation((payload: CreateRolePayload) =>
    clinicApi.post<ClinicRole>('roles', payload),
  );
}

export function useUpdateRole() {
  return useRoleMutation(({ id, patch }: { id: string; patch: UpdateRolePayload }) =>
    clinicApi.patch<ClinicRole>(`roles/${id}`, patch),
  );
}

export function useDeleteRole() {
  return useRoleMutation((id: string) =>
    clinicApi.delete<{ deleted: true; unassignedMemberCount: number }>(`roles/${id}`),
  );
}

export function useSetMemberRoles() {
  return useRoleMutation(({ id, payload }: { id: string; payload: SetMemberRolesPayload }) =>
    clinicApi.put<ClinicMember>(`users/${id}/roles`, payload),
  );
}
